import React, { useState, useImperativeHandle, forwardRef, useEffect } from "react";
import { useAppSelector } from "../../../../app/store";
import { selectAllPartiesInvolved, selectPartiesInvolvedStatus } from "../../../partiesinvolved/logic/PartiesInvolvedSelectors";
import { PartiesInvolvedSkeletonLoader } from "../../../partiesinvolved/components/PartiesInvolvedSidebar";
import { FICPartyInvolvedCard } from "../../components/fic/FICPartyCard";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "../../../../app/hooks";
import { upsertPartiesInvolvedThunk } from "../../../../features/partiesinvolved/logic/PartiesInvolvedActionsThunk";

export interface FICPartiesSectionRef {
  reset: () => void;
  hasChanges: ()=> boolean;
  save: () => Promise<void>;
}

export interface FICPartiesInvolvedSectionProps {
  onNotifyChange: () => void; 
}

export const FICPartiesInvolvedSection = forwardRef<FICPartiesSectionRef, FICPartiesInvolvedSectionProps>(
  ({ onNotifyChange }, ref) => {
    const parties = useAppSelector(selectAllPartiesInvolved);
    const partiesStatus = useAppSelector(selectPartiesInvolvedStatus);

    const [partyChanges, setPartyChanges] = useState<Record<string, { is_fraud?: boolean; generate_ro?: boolean }>>({});
    const [resetKey, setResetKey] = useState(0);

    const isPartiesLoading = partiesStatus === "loading";
    const dispatch = useAppDispatch();

    const getPayload = () => {
      return Object.entries(partyChanges).map(([id, changes]) => ({
        party_id: id,
        name: parties.find((p) => p.party_id === id)?.name,
        ...changes,
      }));
    };

     const { id: case_id } = useParams<{ id: string }>();

  useImperativeHandle(ref, () => ({

    reset: () => {
      setPartyChanges({});
      setResetKey((prev) => prev + 1);
    },

    hasChanges: () => Object.keys(partyChanges).length > 0,
       
    save: async () => {
      if (!case_id) return;

      const payload = getPayload();
      if (payload.length > 0) {
        await dispatch(
          upsertPartiesInvolvedThunk({ 
            case_id, 
            request: { parties_involved: payload } 
          })
        ).unwrap();
        
        setPartyChanges({}); 
      }
    },
  }));

const handleFraudStatusChange = (partyId: string, value: "fraud" | "not-fraud") => {
      setPartyChanges((prev) => ({
      ...prev,
      [partyId]: { ...prev[partyId], is_fraud: value === "fraud" },
      }));
};

const handleGenerateROChange = (partyId: string, checked: boolean) => {
      setPartyChanges((prev) => ({
      ...prev,
      [partyId]: { ...prev[partyId], generate_ro: checked },
      }));
};

  useEffect(() => {
    onNotifyChange();
  }, [partyChanges, onNotifyChange]);

    if (isPartiesLoading) return <PartiesInvolvedSkeletonLoader />;

    return (
      <div key={resetKey}>
        {parties.map((party) => (
          <FICPartyInvolvedCard
            key={party.party_id}
            partyName={party.name}
            partyType={party.party_type}
            fraudStatus={
              partyChanges[party.party_id]?.is_fraud !== undefined
                ? partyChanges[party.party_id].is_fraud ? "fraud" : "not-fraud"
                : party.is_fraud === true ? "fraud" :party.is_fraud === false ? "not-fraud" : undefined
            }
            generateRO={
              partyChanges[party.party_id]?.generate_ro !== undefined
                ? partyChanges[party.party_id].generate_ro
                : party.generate_ro
            }
            generateRODisabled={party.generate_ro === true}
            onFraudStatusChange={(value) => handleFraudStatusChange(party.party_id, value)}
            onGenerateROChange={(checked) => handleGenerateROChange(party.party_id, checked)}
          />
        ))}
      </div>
    );
  }
);

FICPartiesInvolvedSection.displayName = "FICPartiesInvolvedSection";